import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Box } from '@wds/box';
import { Button, Typography } from '@sm/wds-react';
import { BasePage, clientErrorHandler } from '@sm/webassets';
import { MetricsTracker, USER_EVENTS, generateMetricsAttribute } from '@sm/metrics';
import { IconCheck } from '@sm/wds-icons';
import MrxSection from './components/mrxSection';
import ExpertServices from './components/testimonialSection';
import AddOnSection from './components/addOnSection';
import NotifyInterestModal from './components/NotifyInterestModal';
import { saveFormData } from '../../components/HubSpotForm/helpers';
import useHubSpotFormAPI from '~pricing/hooks/useHubSpotAPI';
import SEO from '~pricing/components/SEO';
import { buildHubspotPayload, cleanUpFormFields } from './helpers';
import UserAccountInfo from './UserInfo.graphql';
import useStyles from './useStyles';
import { LinkIcon, SSOSVG } from './assets';

const SSO_PROGRAM_ID = '7014V000002hM6XQAU';
export default function EnterpriseFeatures() {
  const { topBanner, bannerTitle, bannerBody, bannerButton, bannerCTA, linkIcon, sectionTitle, centerText, checkMark } =
    useStyles();
  const [user, setUser] = useState({});
  const [features, setFeatures] = useState([]);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { data } = useHubSpotFormAPI('1a594719-3256-4c2f-b2ea-06fcb43de45f');
  const formFields = useMemo(() => data?.fields, [data?.fields]);
  const ssoInterestSent = localStorage.getItem('pnp-notify-interest-sso');

  useEffect(() => {
    MetricsTracker.track({
      name: USER_EVENTS.PAGE_VIEW,
      data: {
        amplitudeEvent: 'page viewed',
        pageName: 'Enterprise PnP Page',
        urlPath: window.location.pathname,
      },
    });
  }, []);

  useQuery(UserAccountInfo, {
    onCompleted(hookData) {
      const { user: userResp } = hookData;
      if (userResp?.features) {
        const userFeatures = userResp.features;
        const activeFeatures = Object.keys(userFeatures).filter(key => {
          const keyValue = !userFeatures[key];
          if (key === 'isTableauEnabled') {
            return keyValue && !userFeatures.powerBIEnabled;
          }
          if (key === 'isPowerBIEnabled') {
            return false;
          }
          return keyValue && key !== '__typename';
        });
        setFeatures(activeFeatures);
        setUser(hookData.user);
      }
    },
  });

  const toggleThankYouModal = open => {
    setShowThankYouModal(open);
  };

  const notifyInterest = async programId => {
    const autoFilledFormData = buildHubspotPayload(user);
    const hubspotUrlRoute = `${data?.portalId}/${data?.guid}`;

    try {
      const res = await saveFormData(
        { formFields: cleanUpFormFields(programId, formFields), formData: autoFilledFormData },
        true,
        hubspotUrlRoute
      );
      if (!res?.status?.errors) {
        setSubmitted(true);
        toggleThankYouModal(true);
      }
    } catch (err) {
      clientErrorHandler.logError(err, 'Error notifying sales team of interest.');
    }
  };

  return (
    <BasePage pageId="Pricing" legacyWeb="billweb" sticky={false}>
      <SEO>
        <Box className={`${topBanner} notranslate`} display="flex" flexAlign="center" flexJustify="center">
          <Box flexDirection="column" className={bannerCTA}>
            <Typography variant="pageTitle" className={bannerTitle}>
              Activate Single Sign-On (SSO)
            </Typography>
            <Typography variant="body" className={bannerBody}>
              Increase security, ease of access, and control by ensuring all SurveyMonkey users within your organization
              are centralized under your SurveyMonkey Enterprise account.{' '}
              <a
                href="https://help.surveymonkey.com/en/surveymonkey/teams/single-sign-on/"
                target="_blank"
                rel="noreferrer"
              >
                Learn more <img src={LinkIcon} className={linkIcon} alt="link" />
              </a>
            </Typography>
            <Button
              variant="solid"
              color="alt"
              disabled={submitted || ssoInterestSent}
              className={bannerButton}
              onClick={() => {
                notifyInterest(SSO_PROGRAM_ID);
                localStorage.setItem('pnp-notify-interest-sso', true);
              }}
              data-sm-metrics={generateMetricsAttribute({
                data: {
                  desiredProduct: 'single sign on',
                  amplitudeEvent: 'pricing clicked enterprise cta',
                },
              })}
            >
              {submitted || ssoInterestSent ? <IconCheck className={checkMark} size="xs" /> : <></>}
              {submitted || ssoInterestSent ? 'Interest Sent' : 'Set it up'}
            </Button>
          </Box>

          <img src={SSOSVG} alt="SSO" />
        </Box>
        {features.length ? (
          <>
            <Typography variant="pageTitle" className={`${sectionTitle} ${centerText}`}>
              Customize your Enterprise experience with add-ons
            </Typography>
            <Typography variant="body" className={centerText}>
              Let us know which value-added features and services will help you achieve your feedback goals, and we’ll
              help you get started.
            </Typography>
            <AddOnSection features={features} user={user} />
          </>
        ) : (
          <></>
        )}
        <ExpertServices user={user} />
        <MrxSection />
        <NotifyInterestModal showModal={showThankYouModal} toggleModal={toggleThankYouModal} />
      </SEO>
    </BasePage>
  );
}
